/**
 * WWCC Card Details Layout
 *
 * Custom section layout for the Australian WWC Check Details step.
 * Extends the standard SectionLayout pattern (BaseLayout + action bar)
 * with a name-matching validation gate (shared NameMismatchModal +
 * useNameMatchGate). See SectionLayout and AfpConsentControl for
 * the base patterns.
 */
import { APIError } from "@certn/api";
import {
  ButtonGroup,
  GhostButton,
  PrimaryButton,
  Space,
  Stack,
  SummaryCard,
  showToast,
  useColors,
} from "@certn/cascadia-react";
import { getTextStyle } from "@certn/cascadia-react/foundations/typography/styles";
import { CascadiaControlProps, withCascadiaControlProps } from "@certn/forms";
import { CascadiaJsonFormContext } from "@certn/forms";
import { Text } from "@certn/forms/src/components/Text";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import { BaseLayoutContent } from "@certn/forms/src/layouts/BaseLayoutContent";
import { hasLabel } from "@certn/forms/src/utils";
import {
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "@certn/forms/src/wrappers/withCascadiaLayoutProps";
import { css } from "@emotion/css";
import { rankWith, uiTypeIs } from "@jsonforms/core";
import { useJsonForms } from "@jsonforms/react";
import * as Sentry from "@sentry/react";
import { createContext, useContext, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { NameMismatchModal } from "../components/NameMismatchModal";
import { UploadControl } from "../controls/upload-control/UploadControl";
import { UploadSummaryControl } from "../controls/upload-control/UploadSummaryControl";
import { useApplication } from "../ApplicationContext";
import { useNameMatchGate } from "../hooks/useNameMatchGate";
import { NameClaim } from "../utils/name-matching";
import { NotStarted } from "./NotStarted";
import { withRequiredField, WwccCardDetails } from "./wwcc-utils";

const WwccCardContext = createContext({
  hasNoCardChecked: false,
});

function WwccUploadControl(props: CascadiaControlProps) {
  const { isSummary: isSummaryCtx } = useContext(CascadiaJsonFormContext);
  const { hasNoCardChecked } = useContext(WwccCardContext);
  const { label } = props;
  if (isSummaryCtx) {
    return <UploadSummaryControl {...props} />;
  }
  return (
    <Stack gap="space-4">
      {label ? <Text style="form/section-heading">{label}</Text> : null}
      <UploadControl {...props} label={""} required={!hasNoCardChecked} />
    </Stack>
  );
}
WwccUploadControl.displayName = "WwccUploadControl";

const wwccUploadRenderer = {
  tester: rankWith(11, uiTypeIs("UploadControl")),
  renderer: withCascadiaControlProps(WwccUploadControl),
};

function WwccCardDetailsLayout(props: CascadiaLayoutProps) {
  const { t } = useTranslation();
  const colors = useColors();
  const { isSummary, isSubmitted, readonly, isSubmitting, values, submit } =
    useContext(CascadiaJsonFormContext);
  const {
    prevSection,
    navigateToSection,
    partiallySaveData,
    completedSections,
  } = useApplication();

  const wwccDetails = (values?.wwcc_card_details ?? {}) as WwccCardDetails;
  const cardName: NameClaim = {
    given_name: wwccDetails.card_given_name,
    additional_name: wwccDetails.card_additional_name,
    family_name: wwccDetails.card_family_name,
  };
  const { showModal, setShowModal, gateSubmit } = useNameMatchGate(cardName);

  const hasUploadedCard = Boolean(values?.wwcc_card_image);
  const hasNoCardChecked = Boolean(wwccDetails.has_no_wwcc_card);
  const canProceed = hasUploadedCard || hasNoCardChecked;

  const jsonForms = useJsonForms();

  /**
   * Custom renderer list that prepends the WWCC upload renderer to allow the
   * correct UI label for the dynamic upload option paired with the tickbox
   * that makes the upload optional.
   *
   * The WWCC upload renderer (wwccUploadRenderer) is defined at module level
   * so its component identity is stable across renders. Without this, React
   * would treat every re-render as a new component type, unmounting the
   * FileUpload mid-upload and silently discarding the selected file.
   * The reactive `hasNoCardChecked` value is passed via WwccCardContext
   * instead of a closure to avoid recreating the component.
   */
  const uploadRenderers = useMemo(
    () => [wwccUploadRenderer, ...(jsonForms.renderers ?? [])],
    [jsonForms.renderers]
  );

  /**
   * Dynamically mark expiry_date as required in the JSON schema when QLD is
   * selected and the applicant has a card. BaseLayout propagates schema via
   * BaseLayoutContext, so nested JsonForms elements pick up the change and
   * the "(optional)" label disappears reactively.
   */
  const schemaWithConditionalExpiry = useMemo(() => {
    const expiryRequired =
      wwccDetails.australian_state === "QLD" && !hasNoCardChecked;
    if (!expiryRequired) return props.schema;
    return withRequiredField(props.schema, "wwcc_card_details", "expiry_date");
  }, [props.schema, wwccDetails.australian_state, hasNoCardChecked]);

  const handleNextStep = () => gateSubmit(submit);

  if (isSummary) {
    const sectionKey = (props.uischema as any)?.name;
    const isCompleted = completedSections?.includes(sectionKey);

    return (
      <SummaryCard header={props.label}>
        <BaseLayout
          testID="section-layout"
          {...props}
          className={styles.summary}
        >
          {!isCompleted && !readonly && (
            <NotStarted
              sectionKey={sectionKey}
              showErrors={Boolean(isSubmitted)}
            />
          )}
        </BaseLayout>
      </SummaryCard>
    );
  }

  return (
    <>
      <BaseLayout {...props} schema={schemaWithConditionalExpiry}>
        <div className={styles.heading}>
          {hasLabel(props.uischema) && props.uischema.label && (
            <h1 className={styles.label}>{props.uischema.label}</h1>
          )}
          {props.uischema.options?.subLabel && (
            <Text style="form/body-primary" color="text-weak">
              {props.uischema.options.subLabel}
            </Text>
          )}
        </div>
        <WwccCardContext.Provider value={{ hasNoCardChecked }}>
          <BaseLayoutContent renderers={uploadRenderers} />
        </WwccCardContext.Provider>
      </BaseLayout>
      <div
        className={css(styles.actionBar, {
          borderTop: `1px solid ${colors["border-base"]}`,
        })}
      >
        <ButtonGroup justify="end">
          {prevSection && (
            <GhostButton
              leftIcon="chevron_left"
              onPress={async () => {
                try {
                  await partiallySaveData();
                } catch (e) {
                  if (e instanceof APIError && e.status === 400) {
                    showToast(t("Error saving your progress"), {
                      type: "error",
                    });
                  } else {
                    showToast(t("Something went wrong, try again"), {
                      type: "error",
                    });
                    Sentry.captureException(e);
                  }
                }

                navigateToSection(prevSection);
              }}
              testID="section-back-button"
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />
          <PrimaryButton
            type="button"
            variant="brand-primary"
            isLoading={isSubmitting}
            isDisabled={!canProceed}
            onPress={handleNextStep}
            testID="section-next-step-button"
          >
            {t("Next step")}
          </PrimaryButton>
        </ButtonGroup>
      </div>
      <NameMismatchModal
        show={showModal}
        onClose={() => setShowModal(false)}
        header={t("Name doesn't match your WWCC details")}
        bodyContent={
          <>
            <p>
              {t(
                "The first and last name entered here must match the legal name or any previous/alias names you have declared on your application."
              )}
            </p>
            <p>
              {t(
                "If the name entered in this form is incorrect, please update it."
              )}
            </p>
            <p>
              <Trans>
                If your WWCC card uses a{" "}
                <strong>previous, maiden, or alias name</strong>, add it in the{" "}
                <strong>Previous Names</strong> section on the{" "}
                <strong>Personal Details</strong> page.
              </Trans>
            </p>
          </>
        }
      />
    </>
  );
}

const styles = {
  summary: css({
    display: "flex",
    flexDirection: "column",
    gap: 0,
  }),
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  label: css(getTextStyle("form/page-title"), { margin: 0 }),
  actionBar: css({
    paddingTop: `calc(${"var(--space-4)"} - 1px)`,
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
  }),
};

WwccCardDetailsLayout.displayName = "WwccCardDetailsSection";

export const WwccCardDetailsLayoutRenderer = withCascadiaLayoutProps(
  WwccCardDetailsLayout
);
